import { Injectable } from '@angular/core';

export type MetricUnit = 'C' | 'km' | 'kPa' | 'kWh/km' | 'm' | 'sqKm';

export type ImperialUnit = 'acre' | 'F' | 'ft' | 'kWh/mi' | 'mi' | 'psi';

export type ConvertibleUnit = MetricUnit | ImperialUnit;

@Injectable({
    providedIn: 'root',
})
export class ConversionService {
    static readonly kmPerMile = 1.609344;
    static readonly metersPerMile = 1609.344;
    static readonly radiansPerDegree = Math.PI / 180;
    static readonly degreesPerRadian = 180 / Math.PI;

    readonly acresPerSqKm = 247.10538146717;
    readonly degreesFPerDegreesC = 1.8;
    readonly degreesOffset = 32;
    readonly feetPerMeter = 3.28084;
    readonly metersPerKm = 1000;
    readonly metersPerMile = ConversionService.metersPerMile;
    readonly kmPerMile = ConversionService.kmPerMile;
    readonly kpaPerPsi = 6.8947572931783;
    readonly sqFeetPerSqMeter = 10.76391041671;
    readonly sqMetersPerAcre = 4046.8564224;
    readonly sqMetersPerHectare = 10000;
    readonly sqMetersPerSqKm = 1000000;
    readonly sqMetersPerSqMile = 2589988.110336;

    readonly cardinalNorth = $localize`North`;
    readonly cardinalNortheast = $localize`Northeast`;
    readonly cardinalEast = $localize`East`;
    readonly cardinalSoutheast = $localize`Southeast`;
    readonly cardinalSouth = $localize`South`;
    readonly cardinalSouthwest = $localize`Southwest`;
    readonly cardinalWest = $localize`West`;
    readonly cardinalNorthwest = $localize`Northwest`;

    readonly cardinalNorthAbbr = $localize`:Cardinal direction North abbreviated:N`;
    readonly cardinalNortheastAbbr = $localize`:Cardinal direction Northeast abbreviated:NE`;
    readonly cardinalEastAbbr = $localize`:Cardinal direction East abbreviated:E`;
    readonly cardinalSoutheastAbbr = $localize`:Cardinal direction Southeast abbreviated:SE`;
    readonly cardinalSouthAbbr = $localize`:Cardinal direction South abbreviated:S`;
    readonly cardinalSouthwestAbbr = $localize`:Cardinal direction Southwest abbreviated:SW`;
    readonly cardinalWestAbbr = $localize`:Cardinal direction West abbreviated:W`;
    readonly cardinalNorthwestAbbr = $localize`:Cardinal direction Northwest abbreviated:NW`;

    private cardinalDirections = [
        this.cardinalNorth,
        this.cardinalEast,
        this.cardinalSouth,
        this.cardinalWest,
    ];

    private cardinalAndInterCardinalDirections = [
        this.cardinalNorth,
        this.cardinalNortheast,
        this.cardinalEast,
        this.cardinalSoutheast,
        this.cardinalSouth,
        this.cardinalSouthwest,
        this.cardinalWest,
        this.cardinalNorthwest,
    ];

    private cardinalDirectionsAbbr = [
        this.cardinalNorthAbbr,
        this.cardinalEastAbbr,
        this.cardinalSouthAbbr,
        this.cardinalWestAbbr,
    ];

    private cardinalAndInterCardinalDirectionsAbbr = [
        this.cardinalNorthAbbr,
        this.cardinalNortheastAbbr,
        this.cardinalEastAbbr,
        this.cardinalSoutheastAbbr,
        this.cardinalSouthAbbr,
        this.cardinalSouthwestAbbr,
        this.cardinalWestAbbr,
        this.cardinalNorthwestAbbr,
    ];

    private unitNormalizationMap = new Map<string, ConvertibleUnit>([
        ['acre', 'acre'],
        ['c', 'C'],
        ['celsius', 'C'],
        ['f', 'F'],
        ['fahrenheit', 'F'],
        ['feet', 'ft'],
        ['foot', 'ft'],
        ['ft', 'ft'],
        ['kilometer', 'km'],
        ['kilometers', 'km'],
        ['kilopascal', 'kPa'],
        ['km', 'km'],
        ['km2', 'sqKm'],
        ['kpa', 'kPa'],
        ['kwh/km', 'kWh/km'],
        ['kwh/mi', 'kWh/mi'],
        ['m', 'm'],
        ['meter', 'm'],
        ['meters', 'm'],
        ['mi', 'mi'],
        ['mile', 'mi'],
        ['miles', 'mi'],
        ['psi', 'psi'],
        ['sqkm', 'sqKm'],
    ]);

    private imperialUnits: ImperialUnit[] = [
        'acre',
        'F',
        'ft',
        'kWh/mi',
        'mi',
        'psi',
    ];
    private metricUnits: MetricUnit[] = [
        'C',
        'km',
        'kPa',
        'kWh/km',
        'm',
        'sqKm',
    ];

    /**
     * @description Converts degrees into radians.
     * @param degrees - Degrees
     * @returns Radians
     */
    static degreesToRadians(degrees: number): number {
        return degrees * ConversionService.radiansPerDegree;
    }

    static kmToMiles(kilometers: number): number {
        return kilometers / ConversionService.kmPerMile;
    }

    static milesToKm(miles: number): number {
        return miles * ConversionService.kmPerMile;
    }

    static milesToMeters(miles: number): number {
        return miles * ConversionService.metersPerMile;
    }

    // #region length
    feetToMeters(feet: number): number {
        return feet / this.feetPerMeter;
    }

    kmToMeters(kilometers: number): number {
        return kilometers * this.metersPerKm;
    }

    kmToMiles(kilometers: number): number {
        return ConversionService.kmToMiles(kilometers);
    }

    metersToFeet(meters: number): number {
        return meters * this.feetPerMeter;
    }

    metersToMiles(meters: number): number {
        return meters / this.metersPerMile;
    }

    metersToKm(meters: number): number {
        return meters / this.metersPerKm;
    }

    milesToKm(miles: number): number {
        return ConversionService.milesToKm(miles);
    }

    milesToMeters(miles: number): number {
        return ConversionService.milesToMeters(miles);
    }
    // #endregion

    // #region area
    acresToSqKm(acres: number): number {
        return acres / this.acresPerSqKm;
    }

    acresToSqMeters(acres: number): number {
        return acres * this.sqMetersPerAcre;
    }

    sqKmToAcres(sqKilometers: number): number {
        return sqKilometers * this.acresPerSqKm;
    }

    sqMetersToAcres(sqMeters: number): number {
        return sqMeters / this.sqMetersPerAcre;
    }

    sqMetersToSqFeet(sqMeters: number): number {
        return sqMeters * this.sqFeetPerSqMeter;
    }

    sqMetersToHectares(sqMeters: number): number {
        return sqMeters / this.sqMetersPerHectare;
    }

    sqMetersToSqKm(sqMeters: number): number {
        return sqMeters / this.sqMetersPerSqKm;
    }

    sqMetersToSqMiles(sqMeters: number): number {
        return sqMeters / this.sqMetersPerSqMile;
    }
    // #endregion

    // #region temp
    celsiusToFahrenheit(tempC: number): number {
        if (!this.isValidNumber(tempC)) {
            return tempC;
        }

        return tempC * this.degreesFPerDegreesC + this.degreesOffset;
    }

    fahrenheitToCelsius(tempF: number): number {
        if (!this.isValidNumber(tempF)) {
            return tempF;
        }

        return (tempF - this.degreesOffset) / this.degreesFPerDegreesC;
    }
    // #endregion

    // #region pressure
    kpaToPsi(kpa: number): number {
        if (!this.isValidNumber(kpa)) {
            return kpa;
        }

        return kpa / this.kpaPerPsi;
    }

    psiToKpa(psi: number): number {
        if (!this.isValidNumber(psi)) {
            return psi;
        }

        return psi * this.kpaPerPsi;
    }
    // #endregion

    // #region efficiency
    kwhPerKmToKwhPerMile(kwhPerKm: number): number {
        if (!this.isValidNumber(kwhPerKm)) {
            return kwhPerKm;
        }

        return kwhPerKm * this.kmPerMile;
    }

    kwhPerMileToKwhPerKm(kwhPerMile: number): number {
        if (!this.isValidNumber(kwhPerMile)) {
            return kwhPerMile;
        }

        return kwhPerMile / this.kmPerMile;
    }
    // #endregion

    isMetricUnit(unit: MetricUnit): boolean {
        return this.metricUnits.includes(unit);
    }

    isImperialUnit(unit: ImperialUnit): boolean {
        return this.imperialUnits.includes(unit);
    }

    normalizeUnit(unit: string): ConvertibleUnit | undefined {
        return this.unitNormalizationMap.get(unit.toLowerCase());
    }

    toImperial(
        value: number,
        sourceUnit: MetricUnit
    ): { value: number; unit: ImperialUnit } {
        switch (sourceUnit) {
            case 'C':
                return {
                    unit: 'F',
                    value: this.celsiusToFahrenheit(value),
                };
            case 'km':
                return {
                    unit: 'mi',
                    value: this.kmToMiles(value),
                };
            case 'kPa':
                return {
                    unit: 'psi',
                    value: this.kpaToPsi(value),
                };
            case 'kWh/km':
                return {
                    unit: 'kWh/mi',
                    value: this.kwhPerKmToKwhPerMile(value),
                };
            case 'm':
                return {
                    unit: 'ft',
                    value: this.metersToFeet(value),
                };
            case 'sqKm':
                return {
                    unit: 'acre',
                    value: this.sqKmToAcres(value),
                };
            default:
                throw new Error(
                    `unrecognized unit "${sourceUnit as string}" converting to imperial`
                );
        }
    }

    toMetric(
        value: number,
        sourceUnit: ImperialUnit
    ): { value: number; unit: MetricUnit } {
        switch (sourceUnit) {
            case 'F':
                return {
                    unit: 'C',
                    value: this.fahrenheitToCelsius(value),
                };
            case 'mi':
                return {
                    unit: 'km',
                    value: this.milesToKm(value),
                };
            case 'psi':
                return {
                    unit: 'kPa',
                    value: this.psiToKpa(value),
                };
            case 'kWh/mi':
                return {
                    unit: 'kWh/km',
                    value: this.kwhPerMileToKwhPerKm(value),
                };
            case 'ft':
                return {
                    unit: 'm',
                    value: this.feetToMeters(value),
                };
            case 'acre':
                return {
                    unit: 'sqKm',
                    value: this.acresToSqKm(value),
                };
            default:
                throw new Error(
                    `unrecognized unit "${sourceUnit as string}" converting to metric`
                );
        }
    }

    /**
     *  Converts an angle in radians to a compass direction
     * @param radians the angle in radians (0 radians corresponds to North)
     * @param abbreviated whether to use the abbreviated version of the direction
     * @param directionType the set of directions to use
     * @returns the direction the angle corresponds to
     */
    radiansToDirection(
        radians: number,
        abbreviated = false,
        directionType: 'fourWind' | 'eightWind' = 'fourWind'
    ): string {
        return this.degreesToDirection(
            radians * ConversionService.degreesPerRadian,
            abbreviated,
            directionType
        );
    }

    /**
     *  Converts an angle in degrees to a compass direction
     * @param degrees the angle in degrees (0 degrees corresponds to North)
     * @param abbreviated whether to use the abbreviated version of the direction
     * @param directionType the set of directions to use
     * @returns the direction the angle corresponds to
     */
    degreesToDirection(
        degrees: number,
        abbreviated = false,
        directionType: 'fourWind' | 'eightWind' = 'fourWind'
    ): string {
        if (!degrees) {
            return '';
        }

        // Normalize the degrees to be between [0, 360)
        degrees %= 360;
        if (degrees < 0) {
            degrees += 360;
        }

        // Select the direction array based on inputs
        let directions = this.cardinalDirections;
        if (directionType === 'fourWind') {
            directions = abbreviated
                ? this.cardinalDirectionsAbbr
                : this.cardinalDirections;
        } else {
            directions = abbreviated
                ? this.cardinalAndInterCardinalDirectionsAbbr
                : this.cardinalAndInterCardinalDirections;
        }

        // Get the portion of degrees each cardinal direction takes up of 360 degrees
        const sectorDegrees = 360 / directions.length;
        // Get the input degrees to a float between 0 and the length of directions array
        const mappedDegrees = degrees / sectorDegrees;
        // Round the mapped degrees to the closest index in directions
        const closestIndex = Math.round(mappedDegrees) % directions.length;
        return directions[closestIndex];
    }

    private isValidNumber(num: number): boolean {
        return typeof num === 'number' && !Number.isNaN(num);
    }
}
